import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  AttachFile,
  Delete,
  ContentCopy,
  CheckCircle,
} from '@mui/icons-material';
import { appStore } from '../stores/AppStore';
import { trackButtonClick, trackExternalMessage } from '../utils/analytics';

interface ExternalMessagePageProps {
  onBack: () => void;
}

const ExternalMessagePage: React.FC<ExternalMessagePageProps> = observer(({ onBack }) => {
  const [messageText, setMessageText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<Map<number, string>>(new Map());
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  // Reset uploading state when code is successfully generated
  useEffect(() => {
    if (appStore.externalMessageCode) {
      // Code is generated, ensure uploading states are reset
    }
  }, [appStore.externalMessageCode]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    trackButtonClick('Select Files', 'External Message Page');
    trackExternalMessage('select_files', `${files.length} files`);

    const newFiles = Array.from(files);
    const updatedFiles = [...selectedFiles, ...newFiles];
    setSelectedFiles(updatedFiles);

    // Create previews for new files
    const newPreviews = new Map(filePreviews);
    newFiles.forEach((file, index) => {
      const fileIndex = selectedFiles.length + index;
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews(prev => new Map(prev).set(fileIndex, e.target?.result as string));
        };
        reader.readAsDataURL(file);
      }
    });

    // Upload files to external session
    try {
      if (appStore.currentExternalSessionId) {
        // Add files to existing session
        await appStore.addFilesToExternalSession(newFiles);
      } else {
        // Create new session with files
        await appStore.createExternalMessageSession(undefined, newFiles);
      }
    } catch (err: any) {
      appStore.showToast(err.message || 'Failed to upload files');
    }

    // Reset file input
    event.target.value = '';
  };

  const handleRemoveFile = async (index: number) => {
    trackButtonClick('Remove File', 'External Message Page');
    const fileToRemove = selectedFiles[index];
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);

    // Remove preview
    const newPreviews = new Map(filePreviews);
    newPreviews.delete(index);
    setFilePreviews(newPreviews);

    // Remove from external session if uploaded
    if (appStore.currentExternalSessionId && fileToRemove) {
      // Note: We need the file URL from the session to remove it
      // For now, we'll just remove it from local state
      // The file will be removed when finalizing if not in selectedFiles
    }
  };

  const handleSendClick = async () => {
    trackButtonClick('Generate Code', 'External Message Page');
    try {
      if (selectedFiles.length > 0) {
        trackExternalMessage('generate_code', 'files');
        // Send files - finalize existing session to get code
        if (appStore.currentExternalSessionId) {
          await appStore.finalizeExternalSession();
        } else {
          appStore.showToast('No session found. Please select files again.');
        }
      } else if (messageText.trim()) {
        trackExternalMessage('generate_code', 'text');
        // Send text - creates session and returns code immediately
        await appStore.createExternalMessageSession(messageText);
      } else {
        appStore.showToast('Please enter text or select files');
      }
    } catch (err: any) {
      appStore.showToast(err.message || 'Failed to create external message');
    }
  };

  const handleCopyCode = async () => {
    if (appStore.externalMessageCode) {
      trackButtonClick('Copy Code', 'External Message Page');
      trackExternalMessage('copy_code', 'external');
      try {
        await navigator.clipboard.writeText(appStore.externalMessageCode);
        setShowCopySuccess(true);
        setTimeout(() => setShowCopySuccess(false), 2000);
        appStore.showToast('Code copied to clipboard');
      } catch (err) {
        console.error('Failed to copy code:', err);
      }
    }
  };

  const handleClose = () => {
    trackButtonClick('Done', 'External Message Page');
    appStore.clearExternalSession();
    setMessageText('');
    setSelectedFiles([]);
    setFilePreviews(new Map());
    setShowCopySuccess(false);
    onBack();
  };

  // Show code prominently at the top if available
  if (appStore.externalMessageCode) {
    return (
      <Box sx={{ p: 2 }}>
        <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(10px)', mb: 2 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 2, opacity: 0.8 }}>
                Your code:
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                  mb: 2,
                  letterSpacing: 2,
                  fontFamily: 'monospace',
                }}
              >
                {appStore.externalMessageCode.match(/.{1,3}/g)?.join(' ') || appStore.externalMessageCode}
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 2, opacity: 0.7 }}>
                Share this 6-digit code to receive the message
              </Typography>
              <Button
                variant="contained"
                startIcon={showCopySuccess ? <CheckCircle /> : <ContentCopy />}
                onClick={handleCopyCode}
                fullWidth
                sx={{
                  mb: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.4)' },
                }}
              >
                {showCopySuccess ? 'Copied!' : 'Copy code'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClose}
                fullWidth
                sx={{ color: '#FFFFFF', borderColor: 'rgba(255, 255, 255, 0.5)' }}
              >
                Done
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(10px)', mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton 
              onClick={() => {
                trackButtonClick('Back', 'External Message Page');
                handleClose();
              }} 
              sx={{ color: '#FFFFFF', mr: 1 }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
              Send to not my device
            </Typography>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter message text..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#FFFFFF',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(255, 255, 255, 0.7)' },
              },
              '& .MuiInputBase-input::placeholder': { color: 'rgba(255, 255, 255, 0.5)' },
            }}
          />

          <input
            type="file"
            id="file-input"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFile />}
              fullWidth
              sx={{
                color: '#FFFFFF',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': { borderColor: 'rgba(255, 255, 255, 0.7)' },
              }}
            >
              Select Files
              <input
                type="file"
                hidden
                multiple
                onChange={handleFileSelect}
              />
            </Button>
          </Box>

          {selectedFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {selectedFiles.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name}
                  onDelete={() => handleRemoveFile(index)}
                  deleteIcon={<Delete />}
                  sx={{
                    mb: 1,
                    mr: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#FFFFFF',
                    '& .MuiChip-deleteIcon': { color: '#FFFFFF' },
                  }}
                />
              ))}
            </Box>
          )}

          {appStore.externalMessageError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {appStore.externalMessageError}
            </Alert>
          )}

          <Button
            variant="contained"
            startIcon={
              appStore.isCreatingExternalMessage || appStore.isUploadingFiles ? (
                <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
              ) : (
                <Send />
              )
            }
            onClick={handleSendClick}
            fullWidth
            disabled={
              (!messageText.trim() && selectedFiles.length === 0) ||
              appStore.isCreatingExternalMessage ||
              appStore.isUploadingFiles
            }
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.4)' },
              '&:disabled': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            {appStore.isUploadingFiles
              ? 'Uploading...'
              : appStore.isCreatingExternalMessage
              ? 'Creating...'
              : 'Generate Code'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
});

export default ExternalMessagePage;

